/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-20 — DỮ LIỆU + TRỤC CHO BIỂU ĐỒ ĐO. Thuần (không React, không DOM): biểu đồ ở `CabinetDetail.tsx`
 * và phép kiểm `server/src/scripts/kiem-tk20.ts` đọc CÙNG một cấu hình — không có bản thứ hai để lệch.
 *
 * Bốn điểm lee chốt 06/10 (từ số đo của AI-SSH):
 *  a) NULL = KHÔNG TẢI ⇒ ngắt đường, KHÔNG vẽ 0, KHÔNG nối qua (`connectNulls` cấm).
 *  b) Công suất vẽ theo `p1`, không theo `power` (tủ trước A7 để `power` NULL 100%).
 *  c) MỘT trục cho V (~237) và A (~0,069) là sai — tỷ lệ 3 500 : 1, đường dòng nằm sát đáy.
 *     ⇒ mỗi đại lượng một TRỤC (`TRUC_CUA`), miền trục tính TRÊN đúng những đường thuộc trục ấy.
 *  d) Cửa sổ không có mẫu ⇒ nói "không có dữ liệu trong khoảng này", không vẽ gì.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

import { donViHien, heSoVeW, theoDonVi, type DonViHien } from './congsuat';

export interface ThongKe { avg: number; min: number; max: number }
export interface OGom { t: string; n: number; v1: ThongKe | null; i1: ThongKe | null; p1: ThongKe | null }
export interface KetQuaGom {
  tu: string; den: string; buocMs: number; soO: number; soOCoMau: number; mauCuoi: string | null; o: OGom[];
}

export type DuongDo = 'v' | 'p' | 'i';
/** Dải [thấp nhất, cao nhất] của MỘT ô. `null` = ô không có số. */
export type Dai = [number, number] | null;
/**
 * Một điểm trên biểu đồ. Giá trị `null` = ô không có số ⇒ khoảng trống.
 * TK-23 — `vDai/pDai/iDai` = min..max của ô: đường avg là XU HƯỚNG, dải là ĐỈNH. Đo 06/10 trên tủ giả: đợt vọt
 *   23,1 kW / 113 A kéo 2 phút, ô 5 phút chỉ hiện avg 15,28 kW — đỉnh bị giấu vì biểu đồ chỉ vẽ avg.
 */
export interface DiemBieuDo {
  ms: number; n: number; v: number | null; p: number | null; i: number | null;
  vDai?: Dai; pDai?: Dai; iDai?: Dai;
}

/** Tên trường dải của một đường. */
export const DAI_CUA: Record<DuongDo, 'vDai' | 'pDai' | 'iDai'> = { v: 'vDai', p: 'pDai', i: 'iDai' };
const daiCua = (t: ThongKe | null): Dai => (t ? [t.min, t.max] : null);

/** ⛔ Đường nào nằm trên trục nào. Đổi `i` sang trục của `v` là đúng lỗi c) — kiem-tk20 bắt. */
export const TRUC_CUA: Record<DuongDo, string> = { v: 'dien-ap', p: 'cong-suat', i: 'dong' };

/** Không đổi `null` thành gì cả. `ms` = giữa ô (điểm vẽ đặt giữa khoảng nó đại diện). */
export function duLieuBieuDo(kq: KetQuaGom): DiemBieuDo[] {
  return kq.o.map((o) => ({
    ms: new Date(o.t).getTime() + kq.buocMs / 2,
    n: o.n,
    v: o.v1 ? o.v1.avg : null,
    p: o.p1 ? o.p1.avg : null,
    i: o.i1 ? o.i1.avg : null,
    vDai: daiCua(o.v1), pDai: daiCua(o.p1), iDai: daiCua(o.i1),
  }));
}

/**
 * Miền một trục = min..max của MỌI đường thuộc trục ấy, chừa 10 % hai đầu. `null` = trục không có số nào.
 * ⛔ TK-23: tính cả DẢI min–max — miền chỉ theo avg thì đỉnh vọt nằm NGOÀI trục và bị cắt mất, đúng cái lỗi đang sửa.
 */
export function mienTruc(diem: readonly DiemBieuDo[], truc: string): [number, number] | null {
  const ds: number[] = [];
  for (const d of diem) for (const k of Object.keys(TRUC_CUA) as DuongDo[]) {
    if (TRUC_CUA[k] !== truc) continue;
    if (d[k] != null) ds.push(d[k] as number);
    const dai = d[DAI_CUA[k]];
    if (dai) ds.push(dai[0], dai[1]);
  }
  if (!ds.length) return null;
  let lo = Math.min(...ds), hi = Math.max(...ds);
  if (hi === lo) { const e = Math.abs(hi) * 0.05 || 1; lo -= e; hi += e; }
  const pad = (hi - lo) * 0.1;
  return [lo - pad, hi + pad];
}

/**
 * "Đọc được" — trung vị của đường nằm trong khoảng 10 %..90 % chiều cao trục của nó. Đường dòng 0,069 A
 * trên trục điện áp 0..260 V nằm ở 0,03 % ⇒ KHÔNG đọc được (bộ phá 3 của TK-20).
 */
export function viTriTrungVi(diem: readonly DiemBieuDo[], k: DuongDo): number | null {
  const m = mienTruc(diem, TRUC_CUA[k]);
  const ds = diem.map((d) => d[k]).filter((x): x is number => x != null).sort((a, b) => a - b);
  if (!m || !ds.length) return null;
  const tv = ds[Math.floor(ds.length / 2)];
  return (tv - m[0]) / (m[1] - m[0]);
}

export const CAU_TRONG = (gio: number) => `Không có dữ liệu trong khoảng này (${gio} giờ qua)`;

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-22 C — TẢI THẬT 20–100 A / ~22 kW. Số trên trục phải VỪA bề rộng trục — ĐO, không đoán.
 *
 * Đo 06/10 (Chrome không đầu, canvas.measureText, 12 px, hai bộ chữ người dùng chọn được):
 *                    "20.000,0"  "999,9"  "100,000"  "100,0"  "22,5"
 *   Lexend             48,9       29,7     45,6       —        23,2
 *   Be Vietnam Pro     55,4       36,3     48,8       —        27,2
 * Chỗ trống cho chữ (Recharts 2.15.4: tickSize 6 + tickMargin 2 = 8 px; biểu đồ lề trái −10, phải +4):
 *   trục TRÁI  = rộng − 8 − 10 · trục PHẢI = rộng − 8 + 4.   Rộng cũ 52 ⇒ trái 34 px, phải 48 px
 *   ⇒ "999,9" W (36,3) cắt ở trục trái, "100,000" A (48,8) cắt ở trục phải với Be Vietnam Pro.
 * Sửa hai chỗ: (1) công suất tự sang kW từ 1 000 W (không còn "20.000,0"); (2) số chữ thập phân theo
 *   ĐỘ LỚN (`chuSoTruc`): 100 A ⇒ "100,0" chứ không "100,000". Rộng 60 ⇒ trái 42, phải 56 px.
 *   Nhãn dài nhất còn lại: "999,9" W = 36,3 px < 42 ✓ · "99,99" A < "100,000" = 48,8 < 56 ✓.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export const BE_RONG_TRUC_Y = 60;

/** Số chữ thập phân cho nhãn trục theo độ lớn của miền: 0,069 A ⇒ 3 · 8,5 A ⇒ 2 · 80 A ⇒ 1 · 1 200 ⇒ 0. */
export function chuSoTruc(mien: readonly [number, number] | null): number {
  const lon = mien ? Math.max(Math.abs(mien[0]), Math.abs(mien[1])) : 0;
  if (lon < 1) return 3;
  if (lon < 10) return 2;
  if (lon < 1000) return 1;
  return 0;
}

/**
 * Đường công suất về ĐƠN VỊ HIỆN (W hoặc kW theo độ lớn của cả cửa sổ), từ đơn vị CÔNG TƠ gửi.
 * Công tơ chưa rõ ⇒ để nguyên, `donViP = null` (không gắn đơn vị đoán).
 */
export function diemTheoDonVi(diem: readonly DiemBieuDo[], donViCongTo: 'W' | 'kW' | null | undefined):
  { diem: DiemBieuDo[]; donViP: DonViHien | null } {
  const heSo = heSoVeW(donViCongTo);
  if (heSo === null) return { diem: [...diem], donViP: null };
  /* Đơn vị chọn theo độ lớn của CẢ dải (đỉnh vọt 23 kW phải đổi trục sang kW dù avg mới 9 kW). */
  let lon = 0;
  for (const d of diem) {
    if (d.p != null) lon = Math.max(lon, Math.abs(d.p * heSo));
    if (d.pDai) lon = Math.max(lon, Math.abs(d.pDai[0] * heSo), Math.abs(d.pDai[1] * heSo));
  }
  const dv = donViHien(lon);
  const doi = (x: number) => theoDonVi(x * heSo, dv);
  return {
    diem: diem.map((d) => ({
      ...d, p: d.p == null ? null : doi(d.p), pDai: d.pDai ? [doi(d.pDai[0]), doi(d.pDai[1])] : d.pDai ?? null,
    })),
    donViP: dv,
  };
}
